/**
 * @copyright WaterStreet. All rights reserved.
 */

/* eslint-disable @typescript-eslint/no-explicit-any */

import {
	Injectable
} from '@angular/core';
import {
	ActivatedRouteSnapshot,
	CanActivate,
	Router,
	UrlTree
} from '@angular/router';
import {
	EntityTypeApiService
} from '@api/services/entities/entity-type.api.service';
import {
	AppConstants
} from '@shared/constants/app.constants';
import {
	AnyHelper
} from '@shared/helpers/any.helper';
import {
	EventHelper
} from '@shared/helpers/event.helper';
import {
	ObjectHelper
} from '@shared/helpers/object.helper';
import {
	IEntityNavigationDefinition
} from '@shared/interfaces/application-objects/entity-navigation-definition.interface';
import {
	IEntityNavigationRequest
} from '@shared/interfaces/application-objects/entity-navigation-request.interface';
import {
	IEntityType
} from '@shared/interfaces/entities/entity-type.interface';
import {
	NavigationService
} from '@shared/services/navigation.service';

/**
 * A route guard that translates simplified deep links for entity instances
 * into canonical entity instance routes with routeData.
 *
 * Expected URL pattern:
 * `:moduleName/entities/:entityType/:id`
 *
 * @export
 * @class EntityDeepLinkTranslatorGuard
 * @implements {CanActivate}
 */
@Injectable({
	providedIn: 'root'
})
export class EntityDeepLinkTranslatorGuard implements CanActivate
{
	/**
	 * Creates an instance of EntityDeepLinkTranslatorGuard.
	 *
	 * @param {Router} router
	 * The router used to create redirect URL trees.
	 * @param {EntityTypeApiService} entityTypeApiService
	 * The entity type API service used to resolve name/group metadata.
	 * @param {NavigationService} navigationService
	 * The centralized navigation service used to resolve navigation definitions.
	 * @memberof EntityDeepLinkTranslatorGuard
	 */
	public constructor(
		private readonly router: Router,
		private readonly entityTypeApiService: EntityTypeApiService,
		private readonly navigationService: NavigationService)
	{
	}

	/**
	 * Resolves a simplified deep link and redirects to a canonical entity route.
	 *
	 * @async
	 * @param {ActivatedRouteSnapshot} route
	 * The activated route snapshot containing deep-link route parameters.
	 * @returns {Promise<boolean | UrlTree>}
	 * A URL tree redirect on success, or false if translation cannot be resolved.
	 * @memberof EntityDeepLinkTranslatorGuard
	 */
	public async canActivate(
		route: ActivatedRouteSnapshot): Promise<boolean | UrlTree>
	{
		try
		{
			const entityTypeRouteToken: string =
				route.paramMap.get('entityType');
			const idValue: string =
				route.paramMap.get('id');
			const id: number = Number(idValue);

			if (AnyHelper.isNullOrWhitespace(entityTypeRouteToken)
				|| !Number.isInteger(id)
				|| id <= 0)
			{
				return this.getFallbackRoute(
					'Unable to open this link. '
						+ 'The entity route format was invalid.');
			}

			const entityType: IEntityType =
				await this.getEntityTypeByGroup(entityTypeRouteToken);

			if (AnyHelper.isNull(entityType))
			{
				return this.getFallbackRoute(
					'Unable to open this link. '
						+ `No entity type matched the group '${entityTypeRouteToken}'.`);
			}

			const navigationDefinition: IEntityNavigationDefinition =
				await this.navigationService
					.getEntityNavigationDefinition(
						<IEntityNavigationRequest>
						{
							id: id,
							entityType: entityType.name,
							entityTypeGroup: entityType.group
						});

			const isGeneratedLayout: boolean =
				navigationDefinition.layoutType
					=== AppConstants.layoutTypes.generated;

			const viewType: string =
				isGeneratedLayout
					? AppConstants.viewTypes.view
					: AppConstants.viewTypes.edit;

			const routeData: any =
				{
					layoutType: navigationDefinition.layoutType,
					...navigationDefinition.additionalRouteData
				};

			return this.router.createUrlTree(
				[
					navigationDefinition.baseUrl,
					navigationDefinition.entityTypeGroup,
					viewType,
					navigationDefinition.id
				],
				{
					queryParams: {
						routeData: ObjectHelper.mapRouteData(routeData)
					}
				});
		}
		catch
		{
			return this.getFallbackRoute(
				'Unable to open this link. An unexpected error occurred '
					+ 'while translating the entity route.');
		}
	}

	/**
	 * Resolves an entity type by group.
	 *
	 * @private
	 * @async
	 * @param {string} entityTypeGroup
	 * The incoming URL segment that represents an entity type group.
	 * @returns {Promise<IEntityType>}
	 * A resolved entity type, or null if no matching type is found.
	 * @memberof EntityDeepLinkTranslatorGuard
	 */
	private async getEntityTypeByGroup(
		entityTypeGroup: string): Promise<IEntityType>
	{
		const byGroup: IEntityType[] =
			await this.entityTypeApiService.query(
				`Group eq '${entityTypeGroup}'`,
				'Id',
				0,
				1);

		if (!AnyHelper.isNullOrEmptyArray(byGroup))
		{
			return byGroup[0];
		}

		return null;
	}

	/**
	 * Returns a fallback dashboard route when deep-link translation fails.
	 *
	 * @private
	 * @param {string} bannerMessage
	 * The user-facing message to display before redirecting.
	 * @returns {UrlTree}
	 * The fallback dashboard URL tree.
	 * @memberof EntityDeepLinkTranslatorGuard
	 */
	private getFallbackRoute(
		bannerMessage: string): UrlTree
	{
		EventHelper.dispatchBannerEvent(
			'Invalid Entity Link',
			`${bannerMessage} Redirecting to the dashboard.`,
			AppConstants.activityStatus.error);

		return this.router.createUrlTree(
			[
				AppConstants.route.dashboardPage
			]);
	}
}
